import constants from "Constant";

import { getDeviceId } from "./device-id";
import { logError } from "./Function";

/**
 * Fire-and-forget event emission to the data platform's /v1/events endpoint.
 *
 * Single generic entry point: callers do `emitEvent("app.opened")`. Any
 * event-specific logic (e.g. the 30-minute throttle on `app.opened`) lives here,
 * keyed by event name — not at the call site — so wiring a new event is just
 * another `emitEvent(...)` call plus, optionally, a row in the rules below.
 *
 * Posts the canonical envelope directly (no Platform proxy). The ingest Lambda
 * stamps `source` (from the API key) and `event_id`, and derives metadata from
 * the request headers (including `X-Device-Id` → `metadata.device_id`) — so the
 * client supplies only name, timestamp, idempotency_key, properties, and user_id
 * when a user is logged in. Anonymous / pre-login loads still emit, with user_id
 * omitted (not sent as null); the device_id keeps them attributable.
 *
 * No-ops silently when the events URL or API key is unset (e.g. local dev, or
 * before the per-env key is populated), mirroring the backend emitters: empty
 * config means skip, never throw.
 */

const USER_ID_KEY = "userID";
const LAST_IDENTIFIED_USER_ID_KEY = "last_identified_user_id";

/**
 * Per-event minimum interval between emissions, in ms. An event listed here is
 * throttled to at most one emit per device per window; events absent from the
 * map are never throttled. `app.opened` fires on every page load, so it's
 * collapsed to a per-device signal (DAU proxy) rather than a raw count.
 */
const THROTTLE_MS: Record<string, number> = {
  "app.opened": 30 * 60 * 1000,
};

/**
 * Events deduped by user id rather than time — one emission per (device, user)
 * pair, forever. `user.identified` binds the anonymous device_id to a user_id;
 * we only need to (re-)fire when that mapping actually changes, so a plain page
 * reload while already signed in is a no-op, while a *different* user landing
 * on the same browser produces a fresh binding row.
 */
const USER_DEDUP_EVENTS = new Set<string>(["user.identified"]);

// Bounded retry for transient delivery failures (429 / 5xx / transport), at
// parity with the Platform (959) and Coach (958) emitters: at most 3 attempts
// with linear backoff (2s, 4s), honouring Retry-After on 429.
export const MAX_ATTEMPTS = 3;
const BACKOFF_MS = 2000;

/** True only when both the ingest URL and the API key are configured. */
function isConfigured(): boolean {
  return Boolean(constants.WONE_DATA_EVENTS_URL && constants.WONE_DATA_API_KEY);
}

/** Transient = worth retrying: throttling (429) or any server-side 5xx. */
function isTransientStatus(status: number): boolean {
  return status === 429 || status >= 500;
}

/**
 * Retry-After in ms, or null when absent/unparseable. Only the integer-seconds
 * form is honoured (matching the backends); an HTTP-date is ignored.
 */
function retryAfterMs(response: Response): number | null {
  const raw = response.headers.get("Retry-After");
  if (raw === null) return null;
  const seconds = Number(raw);
  return Number.isInteger(seconds) && seconds >= 0 ? seconds * 1000 : null;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Apply the per-event throttle for `name`/`deviceId`. Returns false if the event
 * is throttled and still within its window. On success it records the new
 * timestamp *before* returning — closing a TOCTOU race where two near-
 * simultaneous loads both read the same stale value and both emit.
 *
 * The key is namespaced by device id, not user id: `app.opened` is a per-device
 * signal that must span the anonymous → logged-in transition. Keying on the user
 * would open a fresh window the moment a user signs in (or switches), so a cold
 * anonymous open followed by the post-login open would double-fire. The device
 * id is stable across that transition (and across logout — see device-id.ts), so
 * one bucket per device collapses those to a single emit.
 */
function passesThrottle(name: string, deviceId: string): boolean {
  const windowMs = THROTTLE_MS[name];
  if (!windowMs) return true;

  const key = `last_${name.replace(/\./g, "_")}_ts_${deviceId}`;
  const now = Date.now();
  const last = Number(localStorage.getItem(key));
  if (last && now - last < windowMs) return false;

  localStorage.setItem(key, String(now));
  return true;
}

/**
 * Apply the per-user dedup for events in USER_DEDUP_EVENTS. Fires only when the
 * current user id differs from the last recorded one for this device — first
 * login, or a *different* user signing in on the same browser. A plain reload
 * while already signed in is suppressed, and StrictMode's dev double-invoke is
 * naturally absorbed because the first emit records the id.
 *
 * Records the new id *before* returning, mirroring passesThrottle's TOCTOU
 * guard. Non-deduped events are unaffected. Returns false and skips recording
 * for anonymous callers — `user.identified` is meaningless without a user id.
 */
function passesUserDedup(name: string, userId: string | null): boolean {
  if (!USER_DEDUP_EVENTS.has(name)) return true;
  if (!userId) return false;

  const last = localStorage.getItem(LAST_IDENTIFIED_USER_ID_KEY);
  if (last === userId) return false;

  localStorage.setItem(LAST_IDENTIFIED_USER_ID_KEY, userId);
  return true;
}

/**
 * Deliver one already-throttled emission, retrying transient failures up to
 * MAX_ATTEMPTS with linear backoff (Retry-After overrides it on a 429). This is
 * the SAME logical emission across attempts — it never re-checks or re-stamps
 * the throttle. Permanent failures (non-429 4xx) are logged once and dropped.
 *
 * Never throws: the loop is awaited only by emitEvent's detached promise, so a
 * failure here can't reach a caller or block the UI (a tab closing mid-retry
 * just drops the event — best-effort by design).
 */
async function deliver(
  name: string,
  payload: object,
  deviceId: string,
): Promise<void> {
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    let response: Response;
    try {
      response = await fetch(constants.WONE_DATA_EVENTS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": constants.WONE_DATA_API_KEY,
          "X-App-Version": constants.APP_VERSION,
          "X-Device-Id": deviceId,
        },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      // Transport/network error — transient.
      if (attempt < MAX_ATTEMPTS) {
        await delay(BACKOFF_MS * attempt);
        continue;
      }
      logError(`Failed to emit event ${name}`, error);
      return;
    }

    if (response.ok) return;

    if (isTransientStatus(response.status) && attempt < MAX_ATTEMPTS) {
      const override = response.status === 429 ? retryAfterMs(response) : null;
      await delay(override ?? BACKOFF_MS * attempt);
      continue;
    }

    // Permanent failure (non-429 4xx), or transient but out of attempts.
    logError(
      `Failed to emit event ${name}`,
      new Error(`events ingest returned ${response.status}`),
    );
    return;
  }
}

export function emitEvent(
  name: string,
  properties: Record<string, unknown> = {},
): void {
  if (!isConfigured()) return;

  // The stable install id: keys the throttle and travels as X-Device-Id so the
  // ingest Lambda can attribute the event (including anonymous opens with no
  // user_id). Resolved after the config check so an unconfigured load neither
  // mints an id nor burns the throttle window without actually sending.
  const deviceId = getDeviceId();
  if (!deviceId) return;

  if (!passesThrottle(name, deviceId)) return;

  // Emit even with no logged-in user: anonymous / pre-login / logged-out page
  // loads are captured too (user_id is omitted from the payload when null).
  const userId = localStorage.getItem(USER_ID_KEY);

  if (!passesUserDedup(name, userId)) return;

  const payload: Record<string, unknown> = {
    name,
    timestamp: new Date().toISOString(),
    // Minted here, not in deliver()'s retry loop, so every retry reuses the same
    // key and the ingest store dedups them to one event.
    idempotency_key: crypto.randomUUID(),
    properties,
  };
  // Include user_id only when present. The ingest validator wants the field
  // absent (not present-but-null) for anonymous opens, mirroring CIO events.
  if (userId) payload.user_id = userId;

  // Fire-and-forget: emitEvent stays synchronous; the retrying delivery runs in
  // a detached promise so it can never block or break the UI. deliver() never
  // throws, but guard the dangling promise anyway so nothing escapes.
  void deliver(name, payload, deviceId).catch((error) => {
    logError(`Failed to emit event ${name}`, error);
  });
}
